import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import servicesData from "./AvailableServicesData";

// Utility: get short text (200 chars)
const getShortText = (description, excerpt, limit = 200) => {
  const text = description || excerpt || "";
  if (text.length <= limit) return text;
  return text.substring(0, limit).trim() + "...";
};

const ServicesSection = () => {
  /* -----------------------------
   * MAIN TYPES (from data)
   * ----------------------------- */
  const mainTypes = useMemo(() => {
    return [...new Set(servicesData.map((s) => s.mainType))];
  }, []);

  /* -----------------------------
   * STATE
   * ----------------------------- */
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState(mainTypes);

  /* -----------------------------
   * TOGGLE MAIN TYPE
   * (at least one must remain)
   * ----------------------------- */
  const toggleType = (type) => {
    if (selectedTypes.includes(type)) {
      if (selectedTypes.length === 1) return; // ❌ prevent unselecting last
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  /* -----------------------------
   * FILTERED SERVICES
   * ----------------------------- */
  const filteredServices = servicesData.filter((service) => {
    const matchesType = selectedTypes.includes(service.mainType);
    const matchesSearch = service.title
      .toLowerCase()
      .includes(search.toLowerCase());

    return matchesType && matchesSearch;
  });

  return (
    <>
      <style>{`
        :root {
          --green: #7ac142;
          --yellow: #ffc107;
          --soft-bg: #eef6ff;
          --text-dark: #111827;
          --text-muted: #6b7280;
          --shadow: 0 20px 50px rgba(0,0,0,.08);
          --shadow-hover: 0 35px 70px rgba(0,0,0,.15);
          --radius: 22px;
        }

        .service-sec {
          background: #fff;
          margin-bottom: 40px;

        }

        .service-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .service-sub {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 3px;
          color: var(--green);
          display: inline-flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
        }

        .service-sub::before,
        .service-sub::after {
          content: "";
          width: 32px;
          height: 2px;
          background: var(--green);
        }

        .service-title {
          font-size: 44px;
          font-weight: 800;
          color: var(--text-dark);
          line-height: 1.2;
        }

        .service-title span {
          color: var(--green);
        }

        /* FILTERS */
        .filters-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 22px;
          margin-bottom: 60px;
        }

        .chip-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: center;
        }

        .type-chip {
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 999px;
          transition: all .25s ease;
        }

        .type-chip.selected {
          background: var(--green);
          color: #fff;
        }

        .type-chip.unselected {
          background: #f1f5f9;
          color: var(--text-dark);
        }

        .type-chip.unselected:hover {
          background: var(--yellow);
        }

        .search-input {
          width: 360px;
          max-width: 90%;
        }

        /* GRID */
        .service-grid {
          display: flex;
          gap: 36px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .service-card {
          width: 360px;
          background: #fff;
          border-radius: var(--radius);
          padding: 18px;
          box-shadow: var(--shadow);
          transition: transform .35s ease, box-shadow .35s ease;
        }

        .service-card:hover {
          transform: translateY(-12px);
          box-shadow: var(--shadow-hover);
        }

        .card-top {
          display: flex;
          justify-content: space-between;
          margin-bottom: 14px;
        }

        .badge {
          background: var(--yellow);
          padding: 7px 16px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 700;
        }

        .arrow-btn {
          background: var(--green);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          color: #fff;
        }

        .service-card:hover .arrow-btn {
          transform: translateX(4px);
          box-shadow: 0 8px 20px rgba(122,193,66,.45);
        }

        .service-img {
          width: 100%;
          height: 220px;
          object-fit: cover;
          border-radius: 16px;
        }

        .service-content {
          background: var(--soft-bg);
          padding: 22px;
          border-radius: 0 0 16px 16px;
        }

        .service-name {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 10px;
        }

        .service-desc {
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.6;
        }
      `}</style>

      <Box className="service-sec">
        {/* HEADER */}
        <Box className="service-header">
          <Box className="service-sub">OUR SERVICES</Box>
          <Typography className="service-title">
            The Services We Provide <br />
            For <span>Our Members</span>
          </Typography>
        </Box>

        {/* FILTERS */}
        <Box className="filters-wrap">
          {/* MAIN TYPE FILTER */}
          <Box className="chip-wrap">
            {mainTypes.map((type) => {
              const selected = selectedTypes.includes(type);
              return (
                <Chip
                  key={type}
                  label={type}
                  onClick={() => toggleType(type)}
                  className={`type-chip ${
                    selected ? "selected" : "unselected"
                  }`}
                />
              );
            })}
          </Box>

          {/* SEARCH */}
          <TextField
            placeholder="Search services..."
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
            InputProps={{
              sx: {
                borderRadius: "999px",
              },
            }}
          />
        </Box>

        {/* SERVICES */}
        <Box className="service-grid">
          {filteredServices.map((service) => (
            <Box key={service.id} className="service-card">
              <Box className="card-top">
                <Box className="badge">{service.mainType}</Box>
                <IconButton className="arrow-btn">
                  <ArrowForwardRoundedIcon />
                </IconButton>
              </Box>

              <img
                src={service.imageUrl}
                alt={service.title}
                className="service-img"
              />

              <Box className="service-content">
                <Typography className="service-name">
                  {service.title}
                </Typography>

                <Typography className="service-desc">
                  {getShortText(service.description, service.excerpt)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default ServicesSection;
